import type { DatePrecision, HistoricalConcert } from "../types";

/** Honest rendering of a fuzzy historical date — the Archive-mode analogue of
 *  `format.ts`'s `hasKnownTime`: NEVER invent precision the source didn't state. A
 *  year-precision claim reads "1808", not "January 1, 1808"; a season reads "Winter
 *  1812/13", not a fabricated day. This is a deliberate, careful port of the Python
 *  reference implementation (`classical_orchestrator/historical/edtf.py::format_span`) —
 *  keep the two in step; a drift here means the frontend and the export disagree about
 *  what a date says. */

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const SEASON_NAMES: Record<number, string> = { 21: "Spring", 22: "Summer", 23: "Autumn", 24: "Winter" };

/** The EDTF season code (21-24) embedded in a "YYYY-NN" `date_edtf` string, stripped of any
 *  trailing `~`/`?`/`%` qualifier. Returns null for a plain calendar month. */
function seasonCode(edtf: string): number | null {
  const body = edtf.replace(/[~?%]+$/, "");
  const m = /^\d{4}-(\d{2})$/.exec(body);
  if (!m) return null;
  const code = Number(m[1]);
  return code >= 21 && code <= 24 ? code : null;
}

/** Render one historical event's date honestly, given only the three fields every
 *  <EvidenceBlock>/card/slider consumer already has (no Date object — a fuzzy range isn't
 *  an instant, so there's nothing valid to construct one from). */
export function formatHistoricalDate(
  dateEdtf: string,
  dateEarliest: string,
  precision: DatePrecision,
): string {
  switch (precision) {
    case "day": {
      const [y, m, d] = dateEarliest.split("-").map(Number);
      return `${MONTH_NAMES[m - 1]} ${d}, ${y}`;
    }
    case "month": {
      const [y, m] = dateEarliest.split("-").map(Number);
      return `${MONTH_NAMES[m - 1]} ${y}`;
    }
    case "season": {
      const year = Number(dateEarliest.slice(0, 4));
      const code = seasonCode(dateEdtf);
      if (code === 24) return `Winter ${year}/${String(year + 1).slice(-2)}`;
      return code != null ? `${SEASON_NAMES[code]} ${year}` : dateEarliest.slice(0, 4);
    }
    case "year":
      return dateEarliest.slice(0, 4);
    case "decade":
      return `${dateEarliest.slice(0, 3)}0s`;
    case "range":
    default:
      return dateEarliest.slice(0, 4); // caller appends the latest year — see formatHistoricalDateRange
  }
}

/** Full display string including the range's closing year ("1828–1830"), the one case
 *  `formatHistoricalDate` alone can't finish because it only receives `date_earliest`. */
export function formatHistoricalDateRange(concert: {
  date_edtf: string; date_earliest: string; date_latest: string; date_precision: DatePrecision;
}): string {
  if (concert.date_precision === "range") {
    return `${concert.date_earliest.slice(0, 4)}–${concert.date_latest.slice(0, 4)}`;
  }
  return formatHistoricalDate(concert.date_edtf, concert.date_earliest, concert.date_precision);
}

/** Weekday label — valid ONLY at day precision, where a real calendar date exists. Every
 *  other precision has no single day to name a weekday for, so callers must gate on
 *  `date_precision === "day"` before calling this (there is no safe fallback to return). */
export function historicalWeekday(dateEarliest: string): string {
  const [y, m, d] = dateEarliest.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(new Date(y, m - 1, d, 12));
}

/** One-line "as of" style summary for a card's date row, folding in the weekday only when
 *  precision allows it — mirrors the shape `formatDateParts` gives the modern card. */
export function formatHistoricalDateLine(concert: HistoricalConcert): string {
  const display = formatHistoricalDateRange(concert);
  if (concert.date_precision !== "day") return display;
  return `${historicalWeekday(concert.date_earliest)}, ${display}`;
}

/** Year integer for slider math — floor/ceil so a partial-year event (e.g. a "Winter
 *  1812/13" season spanning into January) is never clipped out of a window that should
 *  include it. */
export function yearOf(isoDate: string): number {
  return Number(isoDate.slice(0, 4));
}
